import ReactGA from 'react-ga4'

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined

/**
 * Initialize Google Analytics 4
 * Only initializes if VITE_GA_MEASUREMENT_ID is set
 */
export const initGA = () => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.initialize(GA_MEASUREMENT_ID)
    console.log('[Analytics] GA4 initialized')
  } else {
    console.log('[Analytics] GA4 not initialized - missing VITE_GA_MEASUREMENT_ID')
  }
}

/**
 * Check if analytics is enabled
 */
export const isAnalyticsEnabled = () => Boolean(GA_MEASUREMENT_ID)

/**
 * Track a custom event
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!GA_MEASUREMENT_ID) return
  
  ReactGA.event({
    action,
    category,
    label,
    value,
  })
}

/**
 * Track page view (automatic with React Router, but useful for SPAs)
 */
export const trackPageView = (path?: string) => {
  if (!GA_MEASUREMENT_ID) return
  
  ReactGA.send({
    hitType: 'pageview',
    page: path || window.location.pathname,
  })
}

// ============================================
// Domain-specific tracking functions
// ============================================

/**
 * Track waitlist signup attempts
 */
export const trackWaitlistSignup = (success: boolean, errorCode?: string) => {
  trackEvent(
    success ? 'waitlist_success' : 'waitlist_failure',
    'waitlist',
    errorCode
  )
}

/**
 * Track scroll depth milestones (25%, 50%, 75%, 100%)
 */
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', 'engagement', `${depth}%`, depth)
}

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (buttonName: string, location?: string) => {
  trackEvent('cta_click', 'engagement', `${buttonName}${location ? ` - ${location}` : ''}`)
}

/**
 * Track when a section becomes visible
 */
export const trackSectionView = (sectionName: string) => {
  trackEvent('section_view', 'engagement', sectionName)
}

/**
 * Track external link clicks
 */
export const trackExternalLink = (url: string, linkName?: string) => {
  trackEvent('external_link', 'navigation', linkName || url)
}

